'use client'

/**
 * Custom hook to manage wallet connection and authentication state
 * Manages:
 * - Wallet connection and disconnection
 * - Account state and chain information
 * - Authentication flow
 * - Analytics tracking
 * - MetaMask event handling
 * - OP Sepolia gasless transactions
 */

// React core imports
import { useRouter } from 'next/navigation'

// External libraries and utilities
import { useLogout, usePrivy } from '@privy-io/react-auth'
import { useChains, useDisconnect } from 'wagmi'
import { sendGTMEvent } from '@next/third-parties/google'
import {
  useAAWalletClient,
  useAccount,
} from '@azuro-org/sdk-social-aa-connector'

// Internal application modules
import { ConnectorName, useChainSpecificWallet } from 'wallet'
import { useUserContext } from 'src/contexts/user'
import { useEffect, useCallback, useRef } from 'react'
import { isGasSponsorshipSupported } from '../config/gasSponsorship'
import { constants } from '@/helpers'

// Module-level guard and function for GTM logout event
let gtmLogoutEventSent = false
function sendLogoutGTMEventOnce(userId, walletAddress) {
  if (gtmLogoutEventSent || !userId) {
    return
  }
  gtmLogoutEventSent = true
  sendGTMEvent({
    event: 'logout_completed',
    user_id: userId,
    login_toolkit: 'privy',
    wallet_address: walletAddress,
  })
}

export const useWallet = () => {
  // Navigation and routing
  const route = useRouter()

  // Authentication state
  const { authenticated, ready, user } = usePrivy()
  const { logoutUser, currentUser } = useUserContext()
  const { logout } = useLogout()
  const { disconnect } = useDisconnect()
  // Account and wallet state
  const {
    address,
    connector,
    isConnected,
    isConnecting,
    isReconnecting,
    chain,
    chainId,
    isAAWallet,
  } = useAccount()

  // Chain-specific wallet functionality
  // const chainSpecificWallet = useChainSpecificWallet()
  // const hasGasSponsorship = isGasSponsorshipSupported(chainId || 0)

  // // Determine which AA wallet to use
  // const isAAWallet = hasGasSponsorship
  //   ? chainSpecificWallet.hasGasSponsorship
  //   : azuroIsAAWallet

  const hasLoggedOut = useRef(false)
  const userRef = useRef(user)
  const currentUserRef = useRef(currentUser)

  // Keep refs up to date
  useEffect(() => {
    userRef.current = user
    currentUserRef.current = currentUser
  }, [user, currentUser])

  /**
   * Handles complete logout process including:
   * - Analytics tracking
   * - User state cleanup
   * - Wallet disconnection
   * - Chain reset
   * - Local storage cleanup
   */
  const logoutApp = useCallback(async () => {
    // console.log('logoutApp called', { hasLoggedOut: hasLoggedOut.current })
    if (hasLoggedOut.current) {
      // console.log('Logout already in progress, skipping')
      return
    }
    hasLoggedOut.current = true
    // Set persistent flag for auto-login detection
    if (typeof window !== 'undefined') {
      localStorage.setItem('hasLoggedOut', 'true')
    }
    // Capture user data before cleanup starts
    const userIdForAnalytics = currentUserRef.current?.id
    const walletAddressForAnalytics =
      userRef.current?.smartWallet?.address || address

    // Centralized GTM event call
    sendLogoutGTMEventOnce(userIdForAnalytics, walletAddressForAnalytics)
    // Cleanup operations
    localStorage.removeItem(constants?.localStorageKeys.loginMethod)
    logoutUser()
    logout()
    disconnect()
    route.refresh()
  }, [address, logoutUser, logout, disconnect, route])

  // Chain and wallet client setup
  const chains = useChains()
  const azuroAAWalletClient = useAAWalletClient()

  // Chain-specific wallet functionality
  const chainSpecificWallet = useChainSpecificWallet()
  const hasGasSponsorship = isGasSponsorshipSupported(chainId || 0)

  // Get the appropriate AA wallet client
  const aaWalletClient = hasGasSponsorship
    ? 'smartAccountClient' in chainSpecificWallet
      ? chainSpecificWallet.smartAccountClient
      : null
    : azuroAAWalletClient

  // Wallet connection type check
  const isWalletConnect = connector?.name === ConnectorName.WalletConnect

  /**
   * Handle MetaMask account change events
   * Automatically logs out user if all accounts are disconnected
   */
  useEffect(() => {
    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0 && !isAAWallet) {
        logoutApp()
      }
    }

    if (typeof window !== 'undefined' && window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged)
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
      }
    }
  }, [logoutApp, isAAWallet])

  return {
    // Authentication state
    authenticated,
    ready,
    user,
    currentUser,

    // Wallet connection state
    account: address as `0x${string}`,
    connector,
    isConnected,
    isConnecting,
    isReconnecting,
    chain,
    chainId,
    isAAWallet,
    aaWalletClient,
    isReady: Boolean(address && !Boolean(user?.id)) || ready,
    // Chain information
    chains,
    isWalletConnect,

    // Actions
    logoutApp,

    // Gas sponsorship (dynamic based on chain configuration)
    hasGasSponsorship,
    executeGaslessTransaction:
      hasGasSponsorship && chainSpecificWallet.executeGaslessTransaction
        ? chainSpecificWallet.executeGaslessTransaction
        : undefined,
  }
}
